import { useState } from "react";
import { toast } from "sonner";
import { X, Check, Edit3 } from "lucide-react";
import DatePickerIT from "./ui/date-picker-it";

/**
 * Blocco 4 · F · Modal "Modifica meta" (personal_goal).
 *
 * Props:
 *  - goal: current goal object
 *  - onClose(): void
 *  - onSaved(updatedGoal): void
 *  - saveFn(patch): Promise<updatedGoal>  — es. api.patchGoal(id, patch)
 */
const HORIZONS = [
  { v: "1_year",   label: "1 anno" },
  { v: "5_years",  label: "5 anni" },
  { v: "10_years", label: "10 anni" },
];

export default function MetaEditModal({ goal, onClose, onSaved, saveFn }) {
  const [form, setForm] = useState({
    title: goal.title || "",
    description: goal.description || "",
    category: goal.category || "",
    horizon: goal.horizon || "1_year",
    target_date: goal.target_date || "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (form.title.trim().length < 3) { toast.error("Il titolo deve avere almeno 3 caratteri"); return; }
    setBusy(true);
    try {
      const patch = {};
      if (form.title !== goal.title) patch.title = form.title;
      if (form.description !== (goal.description || "")) patch.description = form.description || null;
      if (form.category !== (goal.category || "")) patch.category = form.category || null;
      if (form.horizon !== goal.horizon) patch.horizon = form.horizon;
      if (form.target_date !== (goal.target_date || "")) patch.target_date = form.target_date || null;
      if (Object.keys(patch).length === 0) { toast("Nessuna modifica"); onClose(); return; }
      const updated = await saveFn(patch);
      toast.success("Meta aggiornata");
      try { onSaved?.(updated); }
      catch (e2) { console.warn("onSaved callback failed:", e2); }
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore aggiornamento meta");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         onClick={onClose} data-testid="meta-edit-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-semibold inline-flex items-center gap-2">
            <Edit3 size={18}/> Modifica meta
          </h3>
          <button onClick={onClose} aria-label="Chiudi"><X size={20}/></button>
        </div>
        <label className="block text-sm">
          <div className="opacity-70 mb-1">Titolo *</div>
          <input className="pxos-input" value={form.title} maxLength={200}
                 onChange={(e) => setForm({ ...form, title: e.target.value })}
                 data-testid="meta-edit-title"/>
        </label>
        <label className="block text-sm">
          <div className="opacity-70 mb-1">Descrizione</div>
          <textarea className="pxos-input" rows={3} value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    data-testid="meta-edit-description"/>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm">
            <div className="opacity-70 mb-1">Categoria</div>
            <input className="pxos-input" value={form.category} maxLength={80}
                   onChange={(e) => setForm({ ...form, category: e.target.value })}
                   data-testid="meta-edit-category"/>
          </label>
          <label className="block text-sm">
            <div className="opacity-70 mb-1">Orizzonte</div>
            <select className="pxos-input" value={form.horizon}
                    onChange={(e) => setForm({ ...form, horizon: e.target.value })}
                    data-testid="meta-edit-horizon">
              {HORIZONS.map((h) => <option key={h.v} value={h.v}>{h.label}</option>)}
            </select>
          </label>
        </div>
        <label className="block text-sm">
          <div className="opacity-70 mb-1">Data target</div>
          <DatePickerIT value={form.target_date}
                        onChange={(v) => setForm({ ...form, target_date: v })}
                        testId="meta-edit-target-date" placeholder="gg/mm/aaaa"/>
        </label>
        <div className="flex gap-2 pt-2 justify-end">
          <button onClick={onClose} className="text-sm underline opacity-70">Annulla</button>
          <button disabled={busy} onClick={submit} className="pxos-btn-primary text-sm" data-testid="meta-edit-submit">
            <Check size={14}/> Salva modifiche
          </button>
        </div>
      </div>
    </div>
  );
}
